/**
 * Search Store — Zustand state for sidebar global search.
 *
 * Receives streaming results from Engine via NotificationBus
 * (event/search.results, event/search.complete, event/search.error).
 */

import type {
    FileSearchResult,
    SearchCompleteEvent,
    SearchErrorEvent,
    SearchMatch,
    SearchResultsEvent,
} from "@magic-md/engine-client/protocol";
import { create } from "zustand";
import { getPlatformService } from "../platform";
import { useEditorStore } from "./editor-store";
import { ensureEngineInitialized } from "./engine-initializer";

// ── Types ──

export interface SearchOptions {
    caseSensitive: boolean;
    useRegex: boolean;
    wholeWord: boolean;
    scope: "sessions" | "workspace";
    includeGlob: string;
    excludeGlob: string;
}

export interface SearchState {
    /** 搜索关键词 */
    query: string;
    /** 搜索选项 */
    options: SearchOptions;
    /** 搜索结果（按文件分组，渐进式累积） */
    results: FileSearchResult[];
    /** 搜索是否进行中 */
    isSearching: boolean;
    /** 当前 searchId（用于关联流式事件） */
    activeSearchId: string | null;
    /** 累计匹配数 */
    totalMatches: number;
    /** 累计文件数 */
    totalFiles: number;
    /** 搜索耗时(ms) */
    searchDuration: number;
    /** 是否结果截断 */
    truncated: boolean;
    /** 当前高亮的结果索引 */
    activeMatchIndex: number;
    /** 折叠状态（key = filePath） */
    collapsedFiles: Set<string>;
    /** 错误信息 */
    errorMessage: string | null;

    // ── Actions ──
    setQuery: (query: string) => void;
    setOptions: (opts: Partial<SearchOptions>) => void;
    executeSearch: () => Promise<void>;
    cancelSearch: () => void;
    clearResults: () => void;
    navigateToMatch: (filePath: string, match: SearchMatch) => void;
    nextMatch: () => void;
    prevMatch: () => void;
    toggleFileCollapse: (filePath: string) => void;
    collapseAll: () => void;
    expandAll: () => void;

    // ── Stream event handlers (called by NotificationBus) ──
    _onSearchResults: (event: SearchResultsEvent) => void;
    _onSearchComplete: (event: SearchCompleteEvent) => void;
    _onSearchError: (event: SearchErrorEvent) => void;
}

// ── Private module state ──

/** Monotonic counter for generating pending search IDs */
let _searchSeq = 0;

/** Buffer for notifications that arrive before the RPC response (race condition) */
type BufferedEvent =
    | { type: "results"; event: SearchResultsEvent }
    | { type: "complete"; event: SearchCompleteEvent }
    | { type: "error"; event: SearchErrorEvent };
const _buffer: BufferedEvent[] = [];

/** Replay buffered events after the real searchId is known */
function replayBuffered(
    searchId: string,
    _get: () => SearchState,
    set: (partial: Partial<SearchState> | ((s: SearchState) => Partial<SearchState>)) => void,
) {
    const toReplay = _buffer.filter((b) => b.event.searchId === searchId);
    _buffer.length = 0; // clear

    for (const b of toReplay) {
        if (b.type === "results") {
            set((state) => ({
                results: [...state.results, ...b.event.files],
                totalMatches: (b.event as SearchResultsEvent).stats.totalMatches,
                totalFiles: (b.event as SearchResultsEvent).stats.totalFiles,
            }));
        } else if (b.type === "complete") {
            const ev = b.event as SearchCompleteEvent;
            set({
                isSearching: false,
                activeSearchId: null,
                totalMatches: ev.stats.totalMatches,
                totalFiles: ev.stats.totalFiles,
                searchDuration: ev.stats.durationMs,
                truncated: ev.stats.truncated,
            });
        } else if (b.type === "error") {
            set({
                isSearching: false,
                activeSearchId: null,
                errorMessage: (b.event as SearchErrorEvent).message,
            });
        }
    }
}

// ── Store ──

export const useSearchStore = create<SearchState>((set, get) => ({
    // Initial state
    query: "",
    options: {
        caseSensitive: false,
        useRegex: false,
        wholeWord: false,
        scope: "workspace",
        includeGlob: "",
        excludeGlob: "",
    },
    results: [],
    isSearching: false,
    activeSearchId: null,
    totalMatches: 0,
    totalFiles: 0,
    searchDuration: 0,
    truncated: false,
    activeMatchIndex: -1,
    collapsedFiles: new Set<string>(),
    errorMessage: null,

    // ── Actions ──

    setQuery: (query) => set({ query }),

    setOptions: (opts) =>
        set((state) => ({
            options: { ...state.options, ...opts },
        })),

    executeSearch: async () => {
        const { query, options, activeSearchId } = get();
        if (!query.trim()) {
            set({ results: [], totalMatches: 0, totalFiles: 0, errorMessage: null });
            return;
        }

        // Cancel previous search
        if (activeSearchId && !activeSearchId.startsWith("pending-")) {
            const engine = getPlatformService().engine;
            engine.searchCancel?.({ searchId: activeSearchId }).catch(() => { });
        }

        // Generate a predictable searchId on the client side so we can
        // match streaming notifications that arrive *before* the RPC
        // response (common for fast in-memory session searches).
        const pendingId = `pending-${++_searchSeq}`;

        // Clear old results, start new search
        set({
            results: [],
            totalMatches: 0,
            totalFiles: 0,
            isSearching: true,
            truncated: false,
            activeMatchIndex: -1,
            errorMessage: null,
            searchDuration: 0,
            activeSearchId: pendingId,
        });

        try {
            // Eagerly initialize engine if not yet ready (lazy init pattern)
            await ensureEngineInitialized();

            const engine = getPlatformService().engine;
            const result = await engine.searchQuery?.({
                query,
                options: {
                    caseSensitive: options.caseSensitive,
                    useRegex: options.useRegex,
                    wholeWord: options.wholeWord,
                    scope: options.scope,
                    includeGlob: options.includeGlob || undefined,
                    excludeGlob: options.excludeGlob || undefined,
                },
            });
            if (result) {
                // Migrate from pendingId to the real server-assigned searchId.
                // Also replay any buffered notifications that arrived with the real id.
                const { activeSearchId: currentId } = get();
                if (currentId === pendingId) {
                    set({ activeSearchId: result.searchId });
                    // Replay buffered events
                    replayBuffered(result.searchId, get, set);
                }
            }
        } catch (err) {
            // Only update if this search is still the active one
            if (get().activeSearchId === pendingId) {
                set({ isSearching: false, activeSearchId: null, errorMessage: String(err) });
            }
            console.error("[search] Engine search failed:", err);
        }
    },

    cancelSearch: () => {
        const { activeSearchId } = get();
        if (activeSearchId) {
            // Only send cancel to engine for real (non-pending) search IDs
            if (!activeSearchId.startsWith("pending-")) {
                const engine = getPlatformService().engine;
                engine.searchCancel?.({ searchId: activeSearchId }).catch(() => { });
            }
            set({ isSearching: false, activeSearchId: null });
        }
    },

    clearResults: () =>
        set({
            results: [],
            totalMatches: 0,
            totalFiles: 0,
            isSearching: false,
            activeSearchId: null,
            truncated: false,
            activeMatchIndex: -1,
            errorMessage: null,
            searchDuration: 0,
        }),

    navigateToMatch: (filePath, match) => {
        // Open file (or activate existing tab) and set cursor to match line
        const editor = useEditorStore.getState();
        editor.openFileFromPath(filePath).then(() => {
            // Update store state
            useEditorStore.getState().setCursorPosition(match.line, match.colStart + 1);
            // Dispatch goto-line event so EditorPanel scrolls CodeMirror to the line
            window.dispatchEvent(
                new CustomEvent("magic-md:goto-line", {
                    detail: { line: match.line, flash: true },
                }),
            );
        });

        // Compute global index
        const { results } = get();
        let idx = 0;
        for (const file of results) {
            if (file.filePath === filePath) {
                const matchIdx = file.matches.indexOf(match);
                if (matchIdx >= 0) {
                    set({ activeMatchIndex: idx + matchIdx });
                    return;
                }
            }
            idx += file.matches.length;
        }
    },

    nextMatch: () => {
        const { results, activeMatchIndex } = get();
        const total = results.reduce((sum, f) => sum + f.matches.length, 0);
        if (total === 0) return;
        const next = (activeMatchIndex + 1) % total;
        const { filePath, match } = getMatchAtIndex(results, next);
        if (filePath && match) {
            get().navigateToMatch(filePath, match);
        }
    },

    prevMatch: () => {
        const { results, activeMatchIndex } = get();
        const total = results.reduce((sum, f) => sum + f.matches.length, 0);
        if (total === 0) return;
        const prev = activeMatchIndex <= 0 ? total - 1 : activeMatchIndex - 1;
        const { filePath, match } = getMatchAtIndex(results, prev);
        if (filePath && match) {
            get().navigateToMatch(filePath, match);
        }
    },

    toggleFileCollapse: (filePath) =>
        set((state) => {
            const next = new Set(state.collapsedFiles);
            if (next.has(filePath)) next.delete(filePath);
            else next.add(filePath);
            return { collapsedFiles: next };
        }),

    collapseAll: () =>
        set((state) => ({
            collapsedFiles: new Set(state.results.map((r) => r.filePath)),
        })),

    expandAll: () => set({ collapsedFiles: new Set<string>() }),

    // ── Stream Event Handlers ──

    _onSearchResults: (event) => {
        const { activeSearchId } = get();
        // If activeSearchId is still a pending-* id, buffer the notification
        if (activeSearchId?.startsWith("pending-") && event.searchId !== activeSearchId) {
            _buffer.push({ type: "results", event });
            return;
        }
        if (event.searchId !== activeSearchId) return;

        set((state) => ({
            results: [...state.results, ...event.files],
            totalMatches: event.stats.totalMatches,
            totalFiles: event.stats.totalFiles,
        }));
    },

    _onSearchComplete: (event) => {
        const { activeSearchId } = get();
        if (activeSearchId?.startsWith("pending-") && event.searchId !== activeSearchId) {
            _buffer.push({ type: "complete", event });
            return;
        }
        if (event.searchId !== activeSearchId) return;

        set({
            isSearching: false,
            activeSearchId: null,
            totalMatches: event.stats.totalMatches,
            totalFiles: event.stats.totalFiles,
            searchDuration: event.stats.durationMs,
            truncated: event.stats.truncated,
        });
    },

    _onSearchError: (event) => {
        const { activeSearchId } = get();
        if (activeSearchId?.startsWith("pending-") && event.searchId !== activeSearchId) {
            _buffer.push({ type: "error", event });
            return;
        }
        if (event.searchId !== activeSearchId) return;

        set({
            isSearching: false,
            activeSearchId: null,
            errorMessage: event.message,
        });
    },
}));

// ── Helpers ──

function getMatchAtIndex(
    results: FileSearchResult[],
    index: number,
): { filePath: string | null; match: SearchMatch | null } {
    let offset = 0;
    for (const file of results) {
        if (index < offset + file.matches.length) {
            return { filePath: file.filePath, match: file.matches[index - offset] };
        }
        offset += file.matches.length;
    }
    return { filePath: null, match: null };
}
